/**
 * Focus Restoration System
 *
 * Extends the existing highlightElement() utility with orchestrator-aware
 * restoration logic. Three main patterns:
 *
 * 1. Return-pulse: When an overlay closes, scroll-to and ring-highlight
 *    the trigger element on the parent surface.
 * 2. Source-active: While a child overlay is open, apply a subtle persistent
 *    highlight to the source element so the user knows which entity is inspected.
 * 3. Keyboard focus: Move keyboard focus to the trigger element for a11y.
 *
 * This module does NOT replace highlightElement() — it wraps and reuses it.
 *
 * @module lib/navigation/focusRestoration
 */

import { highlightElement } from '@/lib/highlightElement';
import type { OriginEnvelope } from './types';
import { emitTelemetry } from './telemetry';

// ── Config ────────────────────────────────────────────────────────────────
const RESTORE_DELAY_MS = 80; // wait for exit animation + host repaint
const RESTORE_RETRY_INTERVAL_MS = 60; // retry interval when element not yet mounted
const RESTORE_MAX_RETRIES = 8; // max retries (~560ms total window)
const HIGHLIGHT_DURATION_MS = 1800;
const FOCUS_HIGHLIGHT_RING =
    'ring-2 ring-primary-400/60 ring-offset-1';

/**
 * Per-surface retry overrides. Register surfaces that need longer
 * retry windows (e.g. data-heavy lists that refetch on drawer close).
 */
const surfaceRetryOverrides = new Map<string, { maxRetries: number; intervalMs: number }>();

/**
 * Register a custom retry budget for a surface family.
 * Call once at module init (e.g. in an integration hook).
 *
 * @example
 * registerRetryOverride('milestone-drawer', { maxRetries: 12, intervalMs: 80 });
 */
export function registerRetryOverride(
    surfaceName: string,
    config: { maxRetries: number; intervalMs: number },
): void {
    surfaceRetryOverrides.set(surfaceName, config);
}

// ── Source-active state tracking ──────────────────────────────────────────
const SOURCE_ACTIVE_CLASS = 'nav-source-active';
const SOURCE_ACTIVE_RING = 'ring-1 ring-primary-300/40';
const activeSourceElements = new Map<string, HTMLElement>();

/**
 * Find an element by entityId using both id and data-highlight-id.
 */
function findElement(elementId: string): HTMLElement | null {
    return (
        document.getElementById(elementId) ??
        document.querySelector<HTMLElement>(`[data-highlight-id="${elementId}"]`)
    );
}

/**
 * Restore visual focus to the trigger element described in an OriginEnvelope.
 * Called when an overlay closes (return-pulse pattern).
 *
 * @param origin  - The OriginEnvelope from the closed overlay
 * @param opts    - Optional overrides
 * @returns true if the element was found and highlighted
 */
export function restoreFocusFromOrigin(
    origin: OriginEnvelope,
    opts?: {
        /** Move keyboard focus to the element (default: false) */
        keyboardFocus?: boolean;
        /** Custom ring class override */
        ringClass?: string;
        /** Highlight duration override */
        duration?: number;
        /** Delay before attempting restoration (default: 80ms) */
        delay?: number;
    },
): boolean {
    const elementId = origin.triggerElementId;
    if (!elementId) return false;

    // Clear source-active highlight first (if still applied)
    clearSourceHighlight(elementId);

    const delay = opts?.delay ?? RESTORE_DELAY_MS;

    // Resolve retry budget — check per-surface override first
    const surfaceName = origin.origin.name;
    const override = surfaceRetryOverrides.get(surfaceName);
    const maxRetries = override?.maxRetries ?? RESTORE_MAX_RETRIES;
    const retryInterval = override?.intervalMs ?? RESTORE_RETRY_INTERVAL_MS;

    // Retry-aware restoration: the target element may not be mounted yet
    // if the parent surface re-renders after the overlay closes (e.g. data
    // refetch, list filter re-mounting, lazy content).
    const attemptRestore = (retriesLeft: number) => {
        requestAnimationFrame(() => {
            const el = findElement(elementId);

            if (!el) {
                // Element not yet in DOM — retry if budget remains
                if (retriesLeft > 0) {
                    setTimeout(
                        () => attemptRestore(retriesLeft - 1),
                        retryInterval,
                    );
                    return;
                }
                // Budget exhausted — emit telemetry, graceful no-op
                emitTelemetry('highlight-restore-failed', {
                    elementId,
                    reason: 'element-not-found',
                    surface: surfaceName,
                    maxRetries,
                });
                return;
            }

            highlightElement(elementId, {
                ringClass: opts?.ringClass ?? FOCUS_HIGHLIGHT_RING,
                duration: opts?.duration ?? HIGHLIGHT_DURATION_MS,
                scrollBlock: 'center',
            });

            // Move keyboard focus
            if (opts?.keyboardFocus) {
                el.focus({ preventScroll: true });
            }

            emitTelemetry('highlight-restore-success', {
                elementId,
                surface: surfaceName,
                retriesUsed: maxRetries - retriesLeft,
            });
        });
    };

    setTimeout(() => attemptRestore(maxRetries), delay);

    return true;
}

/**
 * Activate a persistent subtle highlight on a source element while
 * a child overlay is open. Pairs with clearSourceHighlight().
 *
 * @param elementId - DOM id or data-highlight-id of the source element
 * @param ringClass - optional custom ring class
 * @returns true if element was found and highlighted
 */
export function activateSourceHighlight(
    elementId: string,
    ringClass?: string,
): boolean {
    // Don't double-apply
    if (activeSourceElements.has(elementId)) return true;

    const el = findElement(elementId);
    if (!el) return false;

    const classes = (ringClass ?? SOURCE_ACTIVE_RING).split(' ').filter(Boolean);
    el.classList.add(SOURCE_ACTIVE_CLASS, ...classes);
    activeSourceElements.set(elementId, el);
    return true;
}

/**
 * Clear source-active highlight from an element.
 * Called automatically by restoreFocusFromOrigin, or manually on overlay close.
 */
export function clearSourceHighlight(elementId: string): void {
    const el = activeSourceElements.get(elementId);
    if (!el) return;

    // Remove all source-active classes
    el.classList.remove(SOURCE_ACTIVE_CLASS);
    const ringClasses = SOURCE_ACTIVE_RING.split(' ').filter(Boolean);
    el.classList.remove(...ringClasses);
    activeSourceElements.delete(elementId);
}

/** Clear all source-active highlights. Called on route change. */
export function clearAllSourceHighlights(): void {
    activeSourceElements.forEach((_el, id) => clearSourceHighlight(id));
}

/**
 * Attempt to restore keyboard focus to the trigger element.
 * If the element is not focusable, does nothing (graceful degradation).
 * Independent of visual highlight — for keyboard users.
 */
export function restoreKeyboardFocus(
    elementId: string,
    opts?: { delay?: number },
): boolean {
    const delay = opts?.delay ?? RESTORE_DELAY_MS;

    setTimeout(() => {
        requestAnimationFrame(() => {
            const el = findElement(elementId);
            if (!el) return;

            // Make focusable if not already
            if (el.tabIndex < 0 && !el.hasAttribute('tabindex')) {
                el.setAttribute('tabindex', '-1');
            }
            el.focus({ preventScroll: true });
        });
    }, delay);

    return true;
}

/**
 * Highlight a specific entity element by its data-entity-id value.
 * Convenience wrapper around highlightElement() using the AX1 stable DOM
 * identity convention: data-entity-id="entityType-entityId".
 */
export function highlightEntity(
    entityType: string,
    entityId: string,
    opts?: {
        ringClass?: string;
        duration?: number;
        scrollBlock?: ScrollLogicalPosition;
    },
): void {
    const compositeId = `${entityType}-${entityId}`;
    highlightElement(compositeId, {
        ringClass: opts?.ringClass ?? FOCUS_HIGHLIGHT_RING,
        duration: opts?.duration ?? HIGHLIGHT_DURATION_MS,
        scrollBlock: opts?.scrollBlock ?? 'center',
    });
}
